import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import { SalvButton, SalvCheckbox, SalvModal, SalvTable, Size } from '@salvapp/ui-components';
import { InternalApiCustomListSchemaField, InternalApiEntityType } from '../../api-internal';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useUpdateCustomListSignificantFields } from './customListSuiActions';
import { showFeedback } from '../../feedback/feedbackReducer';

interface Props {
  listId: string;
  entityType: InternalApiEntityType;
  schemaFields: InternalApiCustomListSchemaField[];
  significantFieldIds: string[];
  onClose: () => void;
  show: boolean;
}

interface FieldRow {
  fieldId: string;
  fieldName: string;
  isSignificant: boolean;
}

const CustomListSignificantFieldsModal = ({
  listId,
  entityType,
  schemaFields,
  significantFieldIds,
  onClose,
  show,
}: Props) => {
  const dispatch = useDispatch();
  const updateMutation = useUpdateCustomListSignificantFields();

  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set(significantFieldIds));

  useEffect(() => {
    setSelectedFieldIds(new Set(significantFieldIds));
  }, [significantFieldIds]);

  const parentFields = useMemo(
    () => schemaFields.filter((field) => !field.parentId).sort((a, b) => a.sortOrder - b.sortOrder),
    [schemaFields]
  );

  const fieldRows: FieldRow[] = useMemo(
    () =>
      parentFields.map((field) => ({
        fieldId: field.id,
        fieldName: field.displayName,
        isSignificant: selectedFieldIds.has(field.id),
      })),
    [parentFields, selectedFieldIds]
  );

  const handleCheckboxChange = (fieldId: string, isSelected: boolean) => {
    setSelectedFieldIds((prev) => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(fieldId);
      } else {
        next.delete(fieldId);
      }
      return next;
    });
  };

  const handleSave = () => {
    onClose();
    dispatch(
      showConfirmationModal({
        title: 'Save new Significant Update Indicators from List records?',
        description:
          'Modifying these settings could significantly increase the number of new alerts or ' +
          'prevent certain alerts from reoccurring. Are you certain you wish to proceed with saving ' +
          'these changes?',
        confirmationText: 'Yes',
        cancelText: 'No, go back',
        onConfirm() {
          updateMutation.mutate(
            {
              entityType,
              _configuration: [{ listId, fields: Array.from(selectedFieldIds) }],
            },
            {
              onSuccess: () => {
                dispatch(showFeedback({ message: 'Significant Update Indicators saved successfully', success: true }));
              },
              onError: () => {
                dispatch(showFeedback({ message: 'Failed to save Significant Update Indicators', success: false }));
              },
            }
          );
        },
        onCancel() {},
      })
    );
  };

  const columnHelper = createColumnHelper<FieldRow>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('fieldName', { header: 'Field Name' }),
      columnHelper.accessor('isSignificant', {
        header: () => <div style={{ textAlign: 'center' }}>Significant</div>,
        cell: ({ row }) => (
          <div className='d-flex justify-content-center'>
            <SalvCheckbox
              ariaLabel={`${row.original.fieldName} significant`}
              selected={selectedFieldIds.has(row.original.fieldId)}
              onChange={(isSelected) => handleCheckboxChange(row.original.fieldId, isSelected)}
            />
          </div>
        ),
      }),
    ],
    [selectedFieldIds]
  );

  return (
    <SalvModal show={show} onHide={onClose}>
      <SalvModal.Header>Significant Update Indicators for {entityType.toLowerCase()}</SalvModal.Header>
      <SalvModal.Body>
        <div className='text-s m-b-16'>
          {entityType === InternalApiEntityType.PERSON
            ? "Select which changes in the List's records are significant enough to entail a full re-screening of the Person during the subsequent screening event."
            : "Select which changes in the List's records are significant enough to entail a match to be removed from the goodlist"}
        </div>
        <SalvTable columns={columns} data={fieldRows} />
      </SalvModal.Body>
      <SalvModal.Footer>
        <SalvButton label='Close' variant='muted' size={Size.MEDIUM} onClick={onClose} />
        <SalvButton
          label='Save'
          variant='primary'
          size={Size.MEDIUM}
          disabled={updateMutation.isPending}
          onClick={handleSave}
        />
      </SalvModal.Footer>
    </SalvModal>
  );
};

export default CustomListSignificantFieldsModal;
